/** The editable fields of a mapping, shared by the create dialog and the
 * detail drawer so the two cannot drift.
 *
 * What is editable depends entirely on the kind:
 *
 * - **alias** (`Additional_Ext_ID`) — everything, including the target tour,
 *   the tags applied on match, and the fan-out targets.
 * - **primary** — an entity's own `custom_id` column. It has no companion
 *   fields at all, so only the value is offered and the rest is hidden rather
 *   than shown disabled, which would imply it could be turned on.
 */
import { Alert, Box, Grid, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import { useCallback } from 'react';

import { tagOptions } from '@/api/bookings';
import { EXT_ID_TYPES, type ExtIdType } from '@/api/extIds';
import * as syncApi from '@/api/sync';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';

export interface TourOpt {
  id: number;
  name: string;
}
export interface TagOpt {
  id: number;
  name: string;
  category?: string | null;
}

export interface ExtIdFormValue {
  custom_id: string;
  id_type: ExtIdType;
  use_for_matching: boolean;
  api_queryable: boolean;
  integration: string;
  restech_account: string;
  comment: string;
  ext_link: string;
  tour: TourOpt | null;
  tags: TagOpt[];
  targets: TourOpt[];
}

export const EMPTY_FORM: ExtIdFormValue = {
  custom_id: '',
  id_type: 'product_id',
  use_for_matching: true,
  api_queryable: true,
  integration: '',
  restech_account: '',
  comment: '',
  ext_link: '',
  tour: null,
  tags: [],
  targets: [],
};

interface Props {
  value: ExtIdFormValue;
  onChange: (next: ExtIdFormValue) => void;
  /** Null until a tenant is chosen — every picker below is tenant-scoped. */
  tenantId: number | null;
  kind: 'primary' | 'alias';
  /** Server-side field errors, keyed by wire field name. */
  errors?: Record<string, string>;
  disabled?: boolean;
  /** Hide the target picker when the tour is fixed (create-from-a-tour). */
  hideTourPicker?: boolean;
}

export function ExtIdFields({
  value,
  onChange,
  tenantId,
  kind,
  errors = {},
  disabled = false,
  hideTourPicker = false,
}: Props) {
  const set = useCallback(
    <K extends keyof ExtIdFormValue>(k: K, v: ExtIdFormValue[K]) => onChange({ ...value, [k]: v }),
    [value, onChange],
  );

  const searchTours = useCallback(
    async (q: string, signal: AbortSignal) =>
      tenantId ? await syncApi.searchTours(tenantId, q, signal) : [],
    [tenantId],
  );

  const searchTags = useCallback(
    async (q: string, signal: AbortSignal) =>
      tenantId
        ? (await tagOptions(tenantId, q, undefined, signal)).map((t) => ({
            id: t.id,
            name: t.name,
            category: t.category?.code ?? null,
          }))
        : [],
    [tenantId],
  );

  const isPrimary = kind === 'primary';

  return (
    <Stack spacing={2}>
      <TextField
        label="External ID"
        value={value.custom_id}
        onChange={(e) => set('custom_id', e.target.value)}
        disabled={disabled}
        error={Boolean(errors.custom_id)}
        helperText={
          errors.custom_id ??
          'The exact string the restech sends. Leading and trailing spaces are stripped.'
        }
        fullWidth
        size="small"
        slotProps={{ htmlInput: { maxLength: 128, style: { fontFamily: 'monospace' } } }}
      />

      {isPrimary ? (
        <Alert severity="info">
          This is the entity&apos;s own external ID. It has no type or flags of its own, and it is
          always used for matching. Changing it also updates the derived id the importer resolves
          against.
        </Alert>
      ) : (
        <>
          {!hideTourPicker && (
            <RemoteAutocomplete<TourOpt>
              label="Maps to tour"
              queryFn={searchTours}
              getOptionLabel={(o) => o.name}
              getOptionKey={(o) => o.id}
              value={value.tour}
              onChange={(v) => set('tour', (v as TourOpt) ?? null)}
              placeholder={tenantId ? 'Search tours…' : 'Pick a tenant first'}
              disabled={disabled || !tenantId}
              fetchOnEmpty
              size="small"
            />
          )}

          <TextField
            select
            label="ID type"
            value={value.id_type}
            onChange={(e) => set('id_type', e.target.value as ExtIdType)}
            disabled={disabled}
            error={Boolean(errors.id_type)}
            helperText={errors.id_type ?? 'A label for ops. Matching never reads it.'}
            fullWidth
            size="small"
          >
            {EXT_ID_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FlagField
                label="Product matching"
                help="Use this ID when matching a booking to a tour. Turning it off parks the mapping without renaming it."
                checked={value.use_for_matching}
                onChange={(v) => set('use_for_matching', v)}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FlagField
                label="Vendor lookups"
                help="Allow sending this ID to the vendor API as a query argument. Turn it off for option IDs and compound keys — the vendor does not know them."
                checked={value.api_queryable}
                onChange={(v) => set('api_queryable', v)}
                disabled={disabled}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Integration"
                value={value.integration}
                onChange={(e) => set('integration', e.target.value)}
                disabled={disabled}
                error={Boolean(errors.integration)}
                helperText={errors.integration ?? 'Optional. Blank = applies to every restech.'}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Restech account"
                value={value.restech_account}
                onChange={(e) => set('restech_account', e.target.value)}
                disabled={disabled}
                error={Boolean(errors.restech_account)}
                helperText={errors.restech_account ?? 'Optional. Blank = every account.'}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>

          <TextField
            label="Comment"
            value={value.comment}
            onChange={(e) => set('comment', e.target.value)}
            disabled={disabled}
            error={Boolean(errors.comment)}
            helperText={
              errors.comment ??
              'Optional. What this mapping is for, or why it is parked — the context that otherwise ends up encoded in the ID itself.'
            }
            fullWidth
            size="small"
            multiline
            minRows={2}
          />

          <TextField
            label="External link"
            value={value.ext_link}
            onChange={(e) => set('ext_link', e.target.value)}
            disabled={disabled}
            error={Boolean(errors.ext_link)}
            helperText={
              errors.ext_link ?? 'Optional. The product in the restech, or the tour on the operator’s site.'
            }
            fullWidth
            size="small"
            placeholder="https://…"
          />

          <RemoteAutocomplete<TagOpt>
            label="Tags applied on match"
            multiple
            queryFn={searchTags}
            getOptionLabel={(o) => (o.category ? `${o.category}: ${o.name}` : o.name)}
            getOptionKey={(o) => o.id}
            value={value.tags}
            onChange={(v) => set('tags', (v as TagOpt[]) ?? [])}
            placeholder={tenantId ? 'Search tags…' : 'Pick a tenant first'}
            disabled={disabled || !tenantId}
            fetchOnEmpty
            size="small"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            Applied to the participant when a booking matches through this mapping.
          </Typography>

          <RemoteAutocomplete<TourOpt>
            label="Also book onto"
            multiple
            queryFn={searchTours}
            getOptionLabel={(o) => o.name}
            getOptionKey={(o) => o.id}
            value={value.targets}
            onChange={(v) => set('targets', (v as TourOpt[]) ?? [])}
            placeholder={tenantId ? 'Search tours…' : 'Pick a tenant first'}
            disabled={disabled || !tenantId}
            fetchOnEmpty
            size="small"
          />
          {value.targets.length > 0 ? (
            <Alert severity="warning" sx={{ mt: -1 }}>
              A booking matching this ID will ALSO be created on{' '}
              {value.targets.length === 1 ? 'this tour' : `these ${value.targets.length} tours`}.
            </Alert>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              Optional. Each tour listed here receives its own booking whenever this ID matches.
            </Typography>
          )}
          {errors.target_ids && (
            <Alert severity="error" sx={{ mt: -1 }}>
              {errors.target_ids}
            </Alert>
          )}
          {errors.tag_ids && (
            <Alert severity="error" sx={{ mt: -1 }}>
              {errors.tag_ids}
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
}

function FlagField({
  label,
  help,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  help: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Switch
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          size="small"
          inputProps={{ 'aria-label': label }}
        />
        <Typography variant="body2">{label}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        {help}
      </Typography>
    </Box>
  );
}
